import { API_TAGS } from '@/shared/api/api-tags';
import { baseApi } from '@/shared/api/base-api';
import {
  buildBranchListQueryString,
  normalizePro20PageResponse,
} from '@/shared/api/normalize-pro20-page';
import { normalizePro20Response } from '@/shared/api/pro20-transform';
import type { Pro20ApiResponse } from '@/shared/types/api-response.types';
import type { PaginatedResponse } from '@/shared/types/api.types';

import type {
  Amenity,
  AmenityOrder,
  AmenityOrderDetails,
  BookingCodeParams,
  CreateAmenityOrderPayload,
  CreateAmenityPayload,
  DeleteAmenityParams,
  GetAmenitiesByBranchParams,
  GetAmenityOrderDetailsParams,
  RemoveAmenityImageParams,
  UpdateAmenityOrderParams,
  UpdateAmenityPayload,
  UpdateFormAmenitiesParams,
  UploadAmenityImageParams,
  UploadAmenityImageResponse,
} from '../types/amenities.types';
import {
  normalizeAmenity,
  normalizeAmenityOrder,
  normalizeAmenityOrderDetails,
  normalizeUploadAmenityImageResponse,
} from '../utils/normalize-amenities';

function mapAmenityListItem(raw: unknown): Amenity | null {
  const amenity = normalizeAmenity(raw);
  if (!amenity || amenity.delete) {
    return null;
  }
  return amenity;
}

export const amenitiesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createAmenity: builder.mutation<Amenity, CreateAmenityPayload>({
      query: (body) => ({
        url: '/amenities/create',
        method: 'POST',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeAmenity(data);
        if (!normalized) {
          throw new Error('Invalid create amenity response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Amenities, id: branchCode },
      ],
    }),
    createAmenityOrder: builder.mutation<AmenityOrder, CreateAmenityOrderPayload>({
      query: (body) => ({
        url: '/amenities/create/amenityorder',
        method: 'POST',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeAmenityOrder(data);
        if (!normalized) {
          throw new Error('Invalid create amenity order response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { bookingCode }) => [
        { type: API_TAGS.Bookings, id: bookingCode },
        { type: API_TAGS.Bookings, id: `amenity-order-${bookingCode}` },
      ],
    }),
    uploadAmenityImage: builder.mutation<
      UploadAmenityImageResponse,
      UploadAmenityImageParams
    >({
      query: ({ amenityCode, formData }) => ({
        url: `/amenities/upload/${amenityCode}/image`,
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeUploadAmenityImageResponse(normalizePro20Response(response)),
    }),
    getAmenitiesByBranch: builder.query<
      PaginatedResponse<Amenity>,
      GetAmenitiesByBranchParams
    >({
      query: ({ branchCode, page, size, sortBy, sortDirection, search }) => ({
        url: `/amenities/get/branchcode/${branchCode}${buildBranchListQueryString(
          {
            page,
            size,
            sortBy,
            sortDirection,
            search,
          },
        )}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>, _meta, arg) =>
        normalizePro20PageResponse(
          normalizePro20Response(response),
          mapAmenityListItem,
          arg.size ?? 20,
        ),
      providesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Amenities, id: branchCode },
      ],
    }),
    getAmenityOrderDetails: builder.query<
      AmenityOrderDetails,
      GetAmenityOrderDetailsParams
    >({
      query: ({ bookingCode, branchCode }) => ({
        url: `/amenities/get/amenityorder/details/${bookingCode}/${branchCode}`,
        method: 'GET',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) =>
        normalizeAmenityOrderDetails(normalizePro20Response(response)),
      providesTags: (_result, _error, { bookingCode }) => [
        { type: API_TAGS.Bookings, id: `amenity-order-${bookingCode}` },
      ],
    }),
    updateAmenity: builder.mutation<Amenity, UpdateAmenityPayload>({
      query: (body) => ({
        url: '/amenities/update',
        method: 'PUT',
        body,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeAmenity(data);
        if (!normalized) {
          throw new Error('Invalid amenity update response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Amenities, id: branchCode },
      ],
    }),
    updateAmenityOrder: builder.mutation<AmenityOrder, UpdateAmenityOrderParams>({
      query: ({ bookingCode, cart }) => ({
        url: `/amenities/update/amenityorder/${bookingCode}`,
        method: 'PUT',
        body: cart,
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeAmenityOrder(data);
        if (!normalized) {
          throw new Error('Invalid amenity order update response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { bookingCode }) => [
        { type: API_TAGS.Bookings, id: bookingCode },
        { type: API_TAGS.Bookings, id: `amenity-order-${bookingCode}` },
      ],
    }),
    updateFormAmenities: builder.mutation<null, UpdateFormAmenitiesParams>({
      query: ({ branchCode, formCode, body }) => ({
        url: `/amenities/updateamenity/${branchCode}/${formCode}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Amenities, id: branchCode },
        { type: API_TAGS.Forms, id: 'LIST' },
      ],
    }),
    deleteAmenity: builder.mutation<null, DeleteAmenityParams>({
      query: ({ branchCode, amenityCode }) => ({
        url: `/amenities/delete/${branchCode}/${amenityCode}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: (_result, _error, { branchCode }) => [
        { type: API_TAGS.Amenities, id: branchCode },
      ],
    }),
    duplicateAmenity: builder.mutation<Amenity, DeleteAmenityParams>({
      query: ({ amenityCode }) => ({
        url: `/amenities/duplicate/${amenityCode}`,
        method: 'PUT',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown>) => {
        const data = normalizePro20Response(response);
        const normalized = normalizeAmenity(data);
        if (!normalized) {
          throw new Error('Invalid duplicate amenity response');
        }
        return normalized;
      },
      invalidatesTags: (_result, _error, { branchCode }) =>
        branchCode ? [{ type: API_TAGS.Amenities, id: branchCode }] : [],
    }),
    deleteAmenityOrder: builder.mutation<null, BookingCodeParams>({
      query: ({ bookingCode }) => ({
        url: `/amenities/delete/amenityorder/${bookingCode}`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
      invalidatesTags: (_result, _error, { bookingCode }) => [
        { type: API_TAGS.Bookings, id: bookingCode },
        { type: API_TAGS.Bookings, id: `amenity-order-${bookingCode}` },
      ],
    }),
    removeAmenityImage: builder.mutation<null, RemoveAmenityImageParams>({
      query: ({ amenityCode }) => ({
        url: `/amenities/remove/${amenityCode}/image`,
        method: 'DELETE',
      }),
      transformResponse: (response: Pro20ApiResponse<unknown> | null) => {
        if (response) {
          normalizePro20Response(response);
        }
        return null;
      },
    }),
  }),
});

export const {
  useCreateAmenityMutation,
  useCreateAmenityOrderMutation,
  useUploadAmenityImageMutation,
  useGetAmenitiesByBranchQuery,
  useLazyGetAmenitiesByBranchQuery,
  useGetAmenityOrderDetailsQuery,
  useLazyGetAmenityOrderDetailsQuery,
  useUpdateAmenityMutation,
  useUpdateAmenityOrderMutation,
  useUpdateFormAmenitiesMutation,
  useDeleteAmenityMutation,
  useDuplicateAmenityMutation,
  useDeleteAmenityOrderMutation,
  useRemoveAmenityImageMutation,
} = amenitiesApi;
